import { inject, Injectable } from '@angular/core';
import { AlgoliaEntity, AlgoliaIndex } from '@marmon/shared/types/algolia';
import {
	PageSize,
	SortDir,
	SortDirMap,
	SortMod,
} from '@marmon/shared/types/pagination';
import { isAdmin, User } from '@marmon/shared/types/user';
import {
	algoliasearch,
	Algoliasearch,
	FacetFilters,
	TagFilters,
} from 'algoliasearch';
import { ALGOLIA } from '../tokens';

export type AlgoliaFetch<T extends AlgoliaEntity> = {
	index: AlgoliaIndex;
	query: string;
	user: User;

	sortDir?: SortMod;
	email?: string;
	/**
	 * id of the authenticated user
	 */
	id?: string;
	sortBy?: keyof T | null;
	page?: number;
	size?: PageSize | 0;
	tagFilters?: TagFilters[];
	facetFilters?: FacetFilters[];
	filters?: string;
	facets?: string[];
	maxValuesPerFacet?: number;
	domain?: string;
};

export type AlgoliaResponse<T> = {
	hits: T[];
	hitsPerPage: number;
	index: AlgoliaIndex;
	nbHits: number;
	nbPages: number;
	page: number;
	query: string;
	params: string;
	facets?: Record<string, Record<string, number>>;
};

const DefaultPageSize = 10;

/**
 * Creates an empty Algolia response. This is used to return an empty response from the Algolia service when the user is not an admin.
 * @param index - The index to fetch from.
 * @param query - The query to fetch.
 * @returns An empty Algolia response.
 */
export function emptyAlgoliaResponse<T>(
	index: AlgoliaIndex,
	query = '',
): AlgoliaResponse<T> {
	return {
		hits: [],
		hitsPerPage: DefaultPageSize,
		index,
		nbHits: 0,
		nbPages: 0,
		page: 0,
		query,
		params: '',
		facets: {},
	};
}

/**
 * Pages, filters, and sorts an in-memory list into an Algolia-shaped response.
 */
export function paginateLocalHits<T>(
	items: readonly T[],
	options: {
		index: AlgoliaIndex;
		query?: string;
		page?: number;
		size?: PageSize | 0;
		sortBy?: keyof T | string | null;
		sortDir?: SortMod;
		match?: (item: T, query: string) => boolean;
	},
): AlgoliaResponse<T> {
	const query = options.query ?? '';
	const needle = query.trim().toLowerCase();
	const match =
		options.match ??
		((item: T) => JSON.stringify(item).toLowerCase().includes(needle));

	let hits = needle
		? items.filter((item) => match(item, needle))
		: [...items];

	const sortBy = options.sortBy;
	if (sortBy) {
		const direction = options.sortDir === -1 ? -1 : 1;
		hits = [...hits].sort((left, right) => {
			const a = String(
				(left as Record<string, unknown>)[sortBy as string] ?? '',
			).toLowerCase();
			const b = String(
				(right as Record<string, unknown>)[sortBy as string] ?? '',
			).toLowerCase();
			return a.localeCompare(b) * direction;
		});
	}

	const size = options.size ?? DefaultPageSize;
	const page = options.page ?? 0;
	const paged =
		size === 0 ? hits : hits.slice(page * size, page * size + size);

	return {
		hits: paged,
		hitsPerPage: size === 0 ? hits.length : size,
		index: options.index,
		nbHits: hits.length,
		nbPages:
			size === 0 ? (hits.length ? 1 : 0) : Math.ceil(hits.length / size),
		page,
		query,
		params: '',
		facets: {},
	};
}

@Injectable({ providedIn: 'root' })
export class AlgoliaService {
	private readonly algolia = inject(ALGOLIA);

	private _client!: Algoliasearch;

	private get client() {
		if (this._client) return this._client;

		const { applicationId, apiKey } = this.algolia;
		this._client = algoliasearch(applicationId, apiKey);
		return this._client;
	}

	fetch<T extends AlgoliaEntity>({
		index,
		query,
		tagFilters,
		facetFilters,
		filters,
		facets,
		maxValuesPerFacet,
		domain,
		email,
		id,
		page,
		size,
		sortDir,
		sortBy,
		user,
	}: AlgoliaFetch<T>): Promise<AlgoliaResponse<T>> {
		const indexName =
			sortBy && sortDir
				? this.getIndexName(
						index,
						sortBy,
						SortDirMap.get(sortDir) ?? 'asc',
					)
				: index;

		tagFilters = tagFilters ?? [];
		facetFilters = facetFilters ?? [];

		if (!isAdmin(user)) {
			if (email) tagFilters.push(`access:${email}`);
			if (id) tagFilters.push(`access:${id}`);
			if (domain) facetFilters.push(`domain:${domain}`);
		}

		return this.client
			.search<T>({
				requests: [
					{
						query,
						indexName,
						tagFilters,
						page: page ?? 0,
						hitsPerPage: size ?? DefaultPageSize,
						facetFilters,
						filters,
						facets,
						maxValuesPerFacet,
					},
				],
			})
			.then(({ results }) => results[0] as AlgoliaResponse<T>);
	}

	/**
	 * Clears the cache for the Algolia instance. This function is index agnostic, it does
	 * not matter what inherited index is cleared, the entire Algolia instance will be cleared so make
	 * sure that it really needs to be done.
	 *
	 * @note this may be needed after deletion or creation of an entity.
	 */
	readonly clearCache = () => this.client.clearCache();

	readonly getIndexName = <T = AlgoliaEntity>(
		parent: AlgoliaIndex,
		sortBy: keyof T,
		sortDir: SortDir,
	) => `${parent}_${String(sortBy)}_${sortDir}`;
}
